/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Icon, Option, Select, Text } from '@tonic-one/react';
import { DeleteIcon } from '@tonic-one/react-icons';
import {
  usePolicyContext,
  storeSelector,
  templateSelector,
} from '@Policy/store';
import {
  ActionEnum,
  DlpPolicyItem,
  KfContainRule,
  KfContainTypeEnum,
  KfFileTypeEnum,
  TableColumn,
} from '@Policy/utils/types';
import isEqual from 'lodash/isEqual';

export const useKfContainRuleListColumns = (
  handleDeleteClick: (index: number) => void,
  handleActionChange: (index: number, action: ActionEnum) => void
) => {
  const { t } = useTranslation();
  // local state
  const kfContainRuleListColumns = React.useMemo(() => {
    const columns: TableColumn[] = [
      {
        id: 'kfFileType',
        header: t('policy.keywordExtraction.header.fileFormat'),
        size: 206,
        style: {
          lineHeight: '30px',
        },
        cell: (row: any) => {
          const { kfFileType } = row.row;
          let kfFileTypeStr = '';
          switch (kfFileType) {
            case KfFileTypeEnum.Text:
              kfFileTypeStr = t('policy.keywordExtraction.option.text');
              break;
            case KfFileTypeEnum.AdobePdf:
              kfFileTypeStr = t('policy.keywordExtraction.option.pdf');
              break;
            case KfFileTypeEnum.MicrosoftDoc:
              kfFileTypeStr = t('policy.keywordExtraction.option.microsoftDoc');
              break;
            default:
              break;
          }
          return <Text>{kfFileTypeStr}</Text>;
        },
      },
      {
        id: 'kfContainType',
        header: t('policy.keywordExtraction.header.condition'),
        cell: (row: any) => {
          const { kfContainType } = row.row;
          return (
            <Text>
              {kfContainType === KfContainTypeEnum.Contain
                ? t('policy.keywordExtraction.option.contains')
                : t('policy.keywordExtraction.option.doesNotContain')}
            </Text>
          );
        },
        size: 100,
      },
      {
        id: 'tmKeywords',
        header: t('policy.keywordExtraction.header.keywords'),
        cell: (row: any) => {
          const { tmKeywords } = row.row;
          return <Text>{tmKeywords.join(';')}</Text>;
        },
        size: 200,
      },
      {
        id: 'action',
        header: t('policy.action'),
        cell: (row: any) => {
          const { kfNonCollaborationActionType } = row.row;
          return (
            <Select
              value={kfNonCollaborationActionType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                handleActionChange(
                  row.id,
                  e.target.value as unknown as ActionEnum
                );
              }}
            >
              <Option value={ActionEnum.quarantine}>
                {t('policy.action.quarantine')}
              </Option>
              <Option value={ActionEnum.delete}>
                {t('policy.action.delete')}
              </Option>
              <Option value={ActionEnum.pass}>{t('policy.action.pass')}</Option>
            </Select>
          );
        },
        size: 200,
      },
      {
        id: 'operator',
        header: '',
        cell: (row: any) => {
          return (
            <Icon
              as={DeleteIcon}
              ml="8px"
              onClick={() => {
                handleDeleteClick(row.id);
              }}
            />
          );
        },
        size: 50,
      },
    ];
    return columns;
  }, []);
  return {
    kfContainRuleListColumns,
  };
};

export const useKfContainRuleListRows = (
  kfContainRuleListColumns: TableColumn[]
) => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(contextStore)<DlpPolicyItem>;
  // get state from store
  const kfContainRules = policySelector(
    (s) => s.tmKeywordfilters?.kfContainRules,
    isEqual
  );
  // get action from store

  const kfContainRuleListRows = React.useMemo(() => {
    const result = kfContainRules.map(
      (row: KfContainRule, rowIndex: number) => {
        return {
          id: `${rowIndex}`,
          cells: kfContainRuleListColumns.map(
            (column: TableColumn, columnIndex: number) => {
              return {
                id: `${columnIndex}_${column.id}`,
                column,
                row,
              };
            }
          ),
          row,
        };
      }
    );
    return result;
  }, [kfContainRuleListColumns, kfContainRules]);
  return {
    kfContainRuleListRows,
  };
};
